/**
 * Role People Section (ORCH-415)
 *
 * Purpose: Who holds a role, on the role page. Collapsed unless the page was
 *          opened at `#people` (the Roles tab's People count links there).
 *          Lists the role's holders in the active tenant through the User
 *          Manager's scoped list (`GET /users?role_id=`), so a scoped user
 *          admin sees only the people they can see. Callers without
 *          `user.view` get a sentence saying why the list isn't there.
 * Dependencies: userManagementService, MDTable (manual mode)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

import MDTable from '../../../components/md-table';
import { StatusChip } from '../../../components/common';
import { userManagementService } from '../../../services/userManagementService';
import { Member } from '../../../types/userManagement';
import { TableColumn } from '../../../types/table';

interface RolePeopleSectionProps {
  roleId: string;
  /** Holders across the tenant (the role's count), which can exceed what the caller sees. */
  assignedCount: number;
  canViewPeople: boolean;
  defaultExpanded: boolean;
}

type PersonRow = Member & { id: string; name: string };

const columns: TableColumn[] = [
  { field: 'name', headerName: 'Name', flex: 1 },
  { field: 'email', headerName: 'Email', flex: 1.5 },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: (value: string) => (
      <StatusChip
        status={value === 'inactive' ? 'disabled' : value}
        label={value === 'inactive' ? 'Inactive' : undefined}
      />
    ),
  },
];

const RolePeopleSection: React.FC<RolePeopleSectionProps> = ({
  roleId,
  assignedCount,
  canViewPeople,
  defaultExpanded,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [search, setSearch] = useState('');
  const [people, setPeople] = useState<PersonRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setExpanded(defaultExpanded), [defaultExpanded]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await userManagementService.listUsers({
        role_id: roleId,
        search: search.trim() || undefined,
        per_page: 200,
      });
      setPeople(
        result.users.map((u) => ({
          ...u,
          id: u.user_id,
          name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email,
        }))
      );
      setTotal(result.total);
    } catch {
      setError('Failed to load the people who hold this role');
    } finally {
      setLoading(false);
    }
  }, [roleId, search]);

  useEffect(() => {
    if (expanded && canViewPeople) load();
  }, [expanded, canViewPeople, load]);

  const outside = Math.max(assignedCount - total, 0);

  return (
    <Accordion
      id="people"
      variant="outlined"
      disableGutters
      expanded={expanded}
      onChange={(_, open) => setExpanded(open)}
      data-testid="role-people-section"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="role-people-toggle">
        <Typography variant="subtitle1" fontWeight={600}>
          People in this tenant ({assignedCount})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {!canViewPeople ? (
          <Typography variant="body2" color="text.secondary" data-testid="role-people-no-access">
            You need access to User Management to see who holds this role.
          </Typography>
        ) : (
          <>
            <TextField
              size="small"
              placeholder="Search people"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{ 'data-testid': 'role-people-search', 'aria-label': 'Search people' }}
              sx={{ maxWidth: 320 }}
            />
            {error && <Alert severity="error">{error}</Alert>}
            {!search && outside > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="role-people-outside"
              >
                {total} shown · {outside} outside your scope or in other tenants
              </Typography>
            )}
            <MDTable
              mode="manual"
              manual={{ columns, title: '', description: '' }}
              data={people}
              loading={loading}
              selectable={false}
              sortable
              onRowClick={(row: PersonRow) => navigate(`/admin/user-management/${row.user_id}`)}
              defaultSortModel={[{ field: 'name', sort: 'asc' }]}
            />
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default RolePeopleSection;
